import _ from 'lodash';
import API from 'services/api.services';
import {
	setCompanyData,
	setCompanyPlan,
	setCompanyPlanId,
	setUserApprovals,
	setUserData,
	setShowPlanSelectionNotification,
	setCompanyPlanNextMonth,
	setCompanyPlanNextMonthId,
} from 'state/reducers/user.reducers';
import { FormInputsTypes } from 'constants/settings.constants';
import {
	setEditSuccess,
	setEditMode,
	setEditError,
	setLoadingSettings,
	setMessage,
	isLoading,
} from 'state/reducers/settings.reducers';
import { intialMessage, userMesaageType } from 'constants/ui.constants';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
	API_GET_CONTACT_COMPANIES,
	API_UPDATE_COMPANY_PLAN,
	API_CONTACT_APPROVALS,
	API_GET_COMPANY_CREDIT_CARD_URL,
} from 'constants/db.constants';
import { store } from 'state/store';
import { Company } from 'constants/company.constants';
import { Plan } from 'constants/plan.constants';
import { setErrorMessage } from 'utils';
import { UserPermission } from 'constants/user.constants';
import { setIsLoading } from 'state/reducers/plan.reducers';
import { updateExistingContacts } from 'state/reducers/contact.reducers';
import { getCompanyDataByCompanyId } from './settings.services';
import { setLoadingApprovals } from 'state/reducers/global.reducers';

export function changeUserData(data: any, formData: any, dataToEdit: FormInputsTypes, api) {
	return async (dispatch: any) => {
		dispatch(setMessage(intialMessage));
		dispatch(setLoadingSettings(true));
		await API.getInstance()
			.post(api, formData)
			.then((response) => {
				dispatch(updateExistingContacts(response.data.contact));
				dispatch(setUserData({ data, dataToEdit }));
				dispatch(setEditSuccess(true));
				dispatch(setEditError(false));
				dispatch(setEditMode(false));
				dispatch(setLoadingSettings(false));
			})
			.catch((error) => {
				setErrorMessage(setMessage, error);
				dispatch(setEditError(true));
				dispatch(setEditSuccess(false));
				dispatch(setLoadingSettings(false));
			});
	};
}

export const getContactCompanies = createAsyncThunk('user/getContactCompanies', (_, { dispatch, rejectWithValue }) =>
	API.getInstance()
		.get(API_GET_CONTACT_COMPANIES)
		.then((response) => {
			const companies = response.data.companies;
			let companyId = store.getState().user.details?.companyId;

			if (companies?.length) {
				let companyData = companies.find((company: Company) => company.id === companyId);
				if (!companyId || !companyData) {
					companyData = companies[0];
				}
				dispatch(setCompanyData(companyData));
			}
			return companies;
		})
		.catch((err) => rejectWithValue(err))
);

export function updateCompanyPlan(newPlan: Plan, firstTimePlanSelection: boolean = false, cancelPlan: boolean = false) {
	return async (dispatch: any) => {
		dispatch(setLoadingSettings(true));
		dispatch(setIsLoading(true));
		dispatch(setMessage(intialMessage));
		const paramsSetting = {
			companyId: store.getState().user.details?.companyId,
			planId: newPlan.id,
			firstPlan: firstTimePlanSelection,
		};
		await API.getInstance()
			.post(API_UPDATE_COMPANY_PLAN, paramsSetting)
			.then((response) => {
				dispatch(setLoadingSettings(false));
				dispatch(setIsLoading(false));
				if (firstTimePlanSelection) {
					//or cancel future plan
					dispatch(setCompanyPlanId(newPlan.id));
					dispatch(setCompanyPlan(newPlan));
					dispatch(setShowPlanSelectionNotification(firstTimePlanSelection));
				} else {
					dispatch(setCompanyPlanNextMonthId(cancelPlan ? null : newPlan.id));
					dispatch(setCompanyPlanNextMonth(cancelPlan ? null : newPlan));
				}
			})
			.catch((error) => {
				dispatch(setLoadingSettings(false));
				dispatch(setIsLoading(false));
				setErrorMessage(setMessage, error);
			});
	};
}

export function getUserApprovals() {
	return async (dispatch: any,getState:any) => {
		dispatch(setLoadingSettings(true));
		dispatch(setLoadingApprovals(true));
		dispatch(setMessage(intialMessage));

		await API.getInstance()
			.get(API_CONTACT_APPROVALS)
			.then((response) => {
				dispatch(setUserApprovals(response.data.data));
				dispatch(setLoadingSettings(false));
			})
			.catch((error) => {
				dispatch(setLoadingSettings(false));
				setErrorMessage(setMessage, error);
			}).finally(()=>{
				dispatch(setLoadingApprovals(false));
			});
	};
}
export function setContactApprovals(payload) {
	return async (dispatch: any) => {
		dispatch(setLoadingSettings(true));
		dispatch(setMessage(intialMessage));
		const { terms, marketing } = payload;
		const paramsSetting = { terms, marketing };
		await API.getInstance()
			.post(API_CONTACT_APPROVALS, paramsSetting)
			.then(async (response) => {
				dispatch(setUserApprovals(response.data.data));
				dispatch(setLoadingSettings(false));
				dispatch(getContactCompanies());
			})
			.catch((error) => {
				dispatch(setLoadingSettings(false));
				setErrorMessage(setMessage, error);
			});
	};
}

export function getCompanyPermissionMapping(payload) {
	const {
		extension_permissions: permission,
		extension_companyIds: companyIdsStr,
		extension_companyPermissionMapping: companyPermissionMappingStr,
	} = payload[0]?.idTokenClaims;

	return companyPermissionMappingStr
		? getCompanyPermissionMappingFromMappingStr(companyPermissionMappingStr)
		: getCompanyPermissionMappingFromCompanyIdsStr(companyIdsStr, permission);
}

const getCompanyPermissionMappingFromCompanyIdsStr = (
	companyIdsStr: string,
	permission: UserPermission
): { [key: number]: UserPermission } => {
	const result: { [key: number]: UserPermission } = {};
	const companyIdsStringsArray = companyIdsStr?.split(',');

	_.forEach(companyIdsStringsArray, (companyId) => {
		result[+companyId] = permission;
	});
	return result;
};

const getCompanyPermissionMappingFromMappingStr = (mappingStr: string): { [key: number]: UserPermission } => {
	const result: { [key: number]: UserPermission } = {};
	const mappingStringsArray = mappingStr.split(',');

	_.forEach(mappingStringsArray, (item) => {
		const [companyId, permission] = item.split('_');
		result[companyId] = permission;
	});

	return result;
};

export const getCompanyCreditCardUrl = createAsyncThunk(
	'fetchData',
	(
		props: {
			companyId: number;
		},
		{ rejectWithValue }
	) => {
		const { companyId } = props;

		return API.getInstance()
			.post(API_GET_COMPANY_CREDIT_CARD_URL, { companyId })
			.then((response) => {
				return response.data;
			})
			.catch((err) => {
				setErrorMessage(setMessage, err);
				return err;
			});
	}
);
